import { type Dispatch, type MutableRefObject } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  storeSharePasscode,
  storeManagementToken,
  getDrafts,
  deleteDraft,
} from '@/lib/storage/local-storage'
import {
  validateShareCreation,
  encryptAndCreateShare,
} from '@/lib/shares/create'
import type { EditorState, ViewMode } from '../state'
import type { EditorAction } from '../state'

interface UseShareCreationOptions {
  userTier: 'free' | 'starter' | 'pro'
  defaultViewMode: ViewMode
  pendingShareAfterSignUp: MutableRefObject<boolean>
  onSettingsClose: () => void
  onShareClose: () => void
  onCreatedOpen: () => void
  onCreatedClose: () => void
  refreshDrafts: () => void
}

/**
 * Handles creating new shares and the post-publish cleanup.
 * - handleCreateShare: validate → encrypt → POST → store passcode/token → dispatch
 * - handleCreatedClose: delete published draft, load next draft, reset options
 */
export function useShareCreation(
  state: EditorState,
  dispatch: Dispatch<EditorAction>,
  options: UseShareCreationOptions,
) {
  const [, setSearchParams] = useSearchParams()
  const {
    userTier,
    defaultViewMode,
    pendingShareAfterSignUp,
    onSettingsClose,
    onShareClose,
    onCreatedOpen,
    onCreatedClose,
    refreshDrafts,
  } = options

  const handleCreateShare = async () => {
    const validation = validateShareCreation({
      content: state.content,
      passcodeOptions: state.passcodeOptions,
      turnstileToken: state.turnstileToken,
      userTier,
    })

    if (!validation.valid) {
      if (validation.needsAuth) {
        onSettingsClose()
        pendingShareAfterSignUp.current = true
        toast.info(validation.error)
        setSearchParams({ auth: 'signup' }, { replace: true })
      } else {
        toast.error(validation.error)
      }
      return
    }

    dispatch({ type: 'SET_LOADING', operation: 'createShare' })
    try {
      const result = await encryptAndCreateShare({
        content: state.content,
        filename: state.filename,
        passcodeOptions: state.passcodeOptions,
        securityOptions: state.securityOptions,
        formatOptions: state.formatOptions,
        turnstileToken: state.turnstileToken,
        userTier,
      })

      window.dispatchEvent(new Event('share-created'))

      // Store passcode and management token locally
      if (!result.isPublic && state.passcodeOptions.passcode) {
        storeSharePasscode(result.shareId, state.passcodeOptions.passcode)
      }
      if (state.securityOptions.isModifiable) {
        storeManagementToken(result.shareId, result.managementToken)
      }

      dispatch({
        type: 'CREATE_SHARE_SUCCESS',
        data: {
          shareId: result.shareId,
          managementToken: result.managementToken,
          passcode: result.passcode,
          expiresAt: result.expiresAt,
          isPublic: result.isPublic,
          filename: result.filename,
          format: result.format,
        },
      })

      onShareClose()
      onCreatedOpen()
    } catch (error) {
      console.error('Failed to create share:', error)
      toast.error('Failed to create share. Please try again.')
      dispatch({ type: 'SET_TURNSTILE_TOKEN', token: null })
    } finally {
      dispatch({ type: 'SET_LOADING', operation: null })
    }
  }

  const handleCreatedClose = () => {
    onCreatedClose()

    if (state.currentDraftId) {
      deleteDraft(state.currentDraftId)
    }

    const remainingDrafts = getDrafts()
    const next = remainingDrafts[0]

    dispatch({
      type: 'POST_PUBLISH_CLEANUP',
      nextDraftId: next?.id ?? null,
      content: next?.content ?? '',
      filename: next?.filename ?? 'untitled.md',
      defaultViewMode,
    })
    refreshDrafts()
  }

  return { handleCreateShare, handleCreatedClose }
}
